import { createContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import type { User, Organization } from '../types'
import type { AuthContextType } from './AuthContextType'
import { supabase } from '../lib/supabase'
import { setUserContext, clearUserContext } from '../config/sentry'

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing session on mount
    checkUser()
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Only handle sign out to avoid loops
      if (event === 'SIGNED_OUT') {
        setUser(null)
        setOrganization(null)
        clearUserContext()
      }
      // Don't call checkUser for other events to avoid infinite loops
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (session?.user) {
        console.log('Session found for user:', session.user.email)
        
        // Fetch user details from the users table WITHOUT join
        // Don't select email since it doesn't exist in the users table
        let { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, organization_id, role, full_name, created_at, updated_at')
          .eq('id', session.user.id)
          .single()
          
        if (userError) {
          console.error('Error fetching user data:', userError)
          
          // If user doesn't exist in users table, try to create it from metadata
          if (userError.code === 'PGRST116') { // Row not found
            const metadata = session.user.user_metadata
            console.log('User metadata:', metadata)
            
            if (metadata.organization_id) {
              // Try to create user record using direct insert with service role
              console.log('Attempting to create missing user record...');
              
              // First, try a direct insert (this might work if RLS allows it)
              const { data: insertData, error: insertError } = await supabase
                .from('users')
                .insert({
                  id: session.user.id,
                  organization_id: metadata.organization_id,
                  full_name: metadata.full_name || `${metadata.first_name || ''} ${metadata.last_name || ''}`.trim() || session.user.email?.split('@')[0] || 'User',
                  role: metadata.role || 'admin',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                })
                .select()
                .single();
                
              if (!insertError && insertData) {
                console.log('User record created successfully');
                userData = insertData;
              } else {
                console.error('Failed to create user record:', insertError);
                // If direct insert fails, set minimal user data from auth
                const userObj = {
                  id: session.user.id,
                  email: session.user.email || '',
                  full_name: metadata.full_name || session.user.email?.split('@')[0] || 'User',
                  role: (metadata.role as 'admin' | 'manager' | 'viewer') || 'admin',
                  organization_id: metadata.organization_id,
                  created_at: session.user.created_at || new Date().toISOString(),
                }
                setUser(userObj);
                
                // Don't block the user from using the app
                console.warn('Using auth metadata for user data');
              }
            }
          }
        }
          
        if (userData) {
          const userObj = {
            id: userData.id,
            email: session.user.email || '',
            full_name: userData.full_name || '',
            role: userData.role as 'admin' | 'manager' | 'viewer',
            organization_id: userData.organization_id,
            created_at: userData.created_at || '',
          }
          setUser(userObj)
          
          // Set Sentry user context (without PII)
          setUserContext(userObj.id, userObj.role)
          
          // Fetch organization separately to avoid join issues
          if (userData.organization_id) {
            const { data: orgData, error: orgError } = await supabase
              .from('organizations')
              .select('*')
              .eq('id', userData.organization_id)
              .single()
            
            if (orgData && !orgError) {
              setOrganization({
                id: orgData.id,
                name: orgData.name,
                industry: orgData.industry,
                employee_count: orgData.employee_count || 0,
                subdomain: orgData.subdomain || '',
                settings: (orgData.settings as Record<string, unknown>) || {},
                created_at: orgData.created_at || '',
              })
            } else if (orgError) {
              console.error('Failed to fetch organization:', orgError);
              // Set a default organization object to prevent UI errors
              setOrganization({
                id: userData.organization_id,
                name: 'Organization',
                industry: 'general',
                employee_count: 0,
                subdomain: '',
                settings: {},
                created_at: new Date().toISOString(),
              });
            }
          } else {
            console.warn('No organization_id found for user');
          }
        }
      } else {
        setUser(null)
        setOrganization(null)
        // Clear Sentry user context
        clearUserContext()
      }
    } catch (error) {
      console.error('Error checking user:', error)
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    if (error) throw error
    await checkUser()
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    setUser(null)
    setOrganization(null)
    // Clear Sentry user context on logout
    clearUserContext()
  }
  
  // Permission helper functions
  const canManageReports = () => {
    return user?.role === 'admin' || user?.role === 'manager'
  }
  
  const canViewReports = () => {
    return user !== null // All authenticated users can view reports
  }
  
  const isAdmin = () => {
    return user?.role === 'admin'
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      organization, 
      loading, 
      signIn, 
      signOut,
      canManageReports,
      canViewReports,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  )
}